import React from 'react'
import { PageProps, graphql } from 'gatsby'
//Mui
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import { useTheme } from '@mui/material/styles'

import KundeserviceLayout from '../../components/Layouts/KundeserviceLayout'
import ContactSectionComponent from '../../components/HelpUniverse/components/ContactSection/ContactSection'

import RichText, { IRichText } from '../../components/RichText'
import Breadcrumbs from '../../components/Breadcrumbs'
import { TCategoryPage } from './types'
import AlertBanner from '../../components/AlertBanner'
import TextComponent from '../../components/Text'
import { IAlertProps } from '../../context/types'
import HelpPageCategoryCard from '../../components/Card/Variants/HelpPageCategoryCard/HelpPageCategoryCard'
import CardComponent from '../../components/Card'
import { ContentfulToComponent, ContentfulToComponentProps } from '../../components/RichText/types'
import Seo, { SEOTypes } from '../../components/Seo'
import ChatManager from '../../components/CogniyChat/ChatManager'
import { IAlertBanner } from '../../components/AlertBanner'
import CarouselLayout from '../../components/Carousel/Variants/HelpCarousel'

export const Head: React.FC<PageProps<CategoryData>> = ({ data }) => {
  const seoProps = data?.page?.seoMetadata?.title &&
    data.page.seoMetadata?.description && {
      ...data.page.seoMetadata,
      lang: 'da',
      createdAt: data.page.createdAt,
      updatedAt: data.page.updatedAt,
    }

  return <>{seoProps && <Seo {...(seoProps as SEOTypes)} />}</>
}
export type CategoryData = Omit<TCategoryPage, '-'> & {
  page: {
    content: [
      {
        text: IRichText
      },
    ]
    internalName: string
    categoryTitle: string
  }
}

interface PageContextProps {
  pageAlert: IAlertProps
  showAlertOnSubpages: boolean
}
const CategoryPageTemplate: React.FC<PageProps<CategoryData, PageContextProps>> = ({ data, pageContext }) => {
  const { breakpoints } = useTheme()
  const styles = {
    categoryPage: {
      padding: '70px 0 20px 0',
      [breakpoints.down('md')]: {
        padding: '120px 8px 40px 8px',
      },
    },
    container: {
      marginTop: '3rem',
    },
  }
  const { content, pageId, internalName, categoryTitle, includeContactSection } = data.page
  const { pageAlert, showAlertOnSubpages } = pageContext
  const analytics = {
    pageName: `${internalName} - kundeservice`,
    siteSection: 'kundeservice',
    subSection1: categoryTitle,
    subSection2: internalName,
    pageType: 'help',
    id: pageId,
  }

  const propsMap: Partial<ContentfulToComponentProps> = {
    HelpUniverseComponentText: <TextComponentProps,>(props: TextComponentProps) => ({
      ...(props as unknown as TextComponentProps),
    }),
    HelpUniversePageSubcategory: <HelpPageCategoryComponentProps,>(props: HelpPageCategoryComponentProps) => ({
      ...(props as unknown as HelpPageCategoryComponentProps),
    }),
    HelpUniverseComponentCard: <CardProps,>(props: CardProps) => ({
      ...(props as unknown as CardProps),
    }),
    HelpUniverseComponentAlert: <IAlertBanner,>(props: IAlertBanner) => ({
      ...(props as unknown as IAlertBanner),
    }),
    HelpUniverseLayoutCarousel: <CarouselLayoutComponent,>(props: CarouselLayoutComponent) => ({
      ...(props as unknown as CarouselLayoutComponent),
    }),
  } as const

  const categoreyComponentMap: Partial<ContentfulToComponent> = {
    'HelpUniverseComponentText': TextComponent,
    'HelpUniversePageSubcategory': HelpPageCategoryCard,
    'HelpUniverseComponentCard': CardComponent,
    'HelpUniverseComponentAlert': AlertBanner,
    'HelpUniverseLayoutCarousel': CarouselLayout,
  } as const

  return (
    <KundeserviceLayout trackingPageInfo={analytics}>
      <Container maxWidth="lg" style={styles.container}>
        <Box sx={styles.categoryPage}>
          <Breadcrumbs />
          {showAlertOnSubpages && pageAlert ? <AlertBanner {...(pageAlert as IAlertBanner)} /> : <></>}
        </Box>
        {content?.map(item => {
          return (
            <RichText
              key={item?.internal.type}
              {...(item?.text as IRichText)}
              componentMap={categoreyComponentMap as ContentfulToComponent}
              propsMap={propsMap as ContentfulToComponentProps}
            />
          )
        })}
      </Container>
      <ChatManager />
      {includeContactSection ? <ContactSectionComponent /> : <></>}
    </KundeserviceLayout>
  )
}

export const pageQuery = graphql`
  query CategoryPageTemplate($id: String!) {
    page: helpUniversePageCategory(contentful_id: { eq: $id }) {
      id
      internal {
        type
      }
      internalName
      pageId: contentful_id
      updatedAt
      categoryTitle
      createdAt
      includeContactSection
      content {
        contentful_id
        internal {
          type
        }
        ... on HelpUniverseComponentText {
          text {
            raw
            references {
              ...CAROUSEL
              ...HELPPAGECATEGORY
              ...HELPUNIVERSEALERTBANNER
            }
          }
        }
      }
      seoMetadata {
        ...HELPPAGESEO
      }
    }
  }
`
export default CategoryPageTemplate
